import { Editor, Extension, NodePos } from '@tiptap/core'
import { EditorState, Plugin, Transaction } from '@tiptap/pm/state'
import { findClosestBlock } from '#docs/utils'

/** Add `save` event in `EditorEvents`. */
declare module '@tiptap/core' {
  interface EditorEvents {
    save: { editor: Editor }
  }
}

export const Keyboard = Extension.create({
  name: 'keyboard',
  addKeyboardShortcuts: () => ({ 'Mod-s': ModS, Enter, Backspace, Delete }),
  addProseMirrorPlugins: () => {
    const edition = new Plugin({ filterTransaction: cancelSubjectEdition })
    return [edition]
  },
})

/** Cancels every operation happening on `<steerlab-subject>` nodes.
 * By design, a question can not be edited as of now.
 * For each step in the transaction, a check is performed to determine if it
 * potentially affects a `<steerlab-subject>`. If that's the case, the
 * transaction is simply cancelled. */
function cancelSubjectEdition(tr: Transaction, state: EditorState) {
  let shouldContinue = true
  tr.steps.forEach(step => {
    step.getMap().forEach((oldStart, oldEnd) => {
      state.doc.nodesBetween(oldStart, oldEnd, node => {
        if (node.type.name === 'steerlabSubject') shouldContinue = false
      })
    })
  })
  return shouldContinue
}

/**  To conform with editor usual standards, pressing `Cmd + s` saves the
 * content of the editor. Even if the content is saved on a regular basis,
 * mapping the command avoid the "Save the page" popup to disturb the user,
 * and potentially force a save for power-users. */
function ModS({ editor }: { editor: Editor }) {
  if (import.meta.env.DEV) console.log('[editor] handleSave')
  editor.emit('save', { editor })
  return true
}

/* Handlers for Enter, Backspace and Delete. Due to the nature of TipTap and
 * the way `contenteditable` works, Enter, Backspace and Delete are overriden,
 * to manipulate correctly `<steerlab-block>` blocks.
 *
 * In the Word editor, every block is designed as a unique `<steerlab-block>`,
 * and everything is written inside a `<steerlab-block>` or a
 * `<steerlab-question>`. Such nodes are open to new extensions in the future.
 *
 * Currently, a `<steerlab-block>` can be deleted, while a `<steerlab-answer>`
 * cannot be deleted. To represent that behaviour, limits have been written:
 * - when pressing Enter, it either enter a newline when in the middle of a
 *   block, or it creates a new `<steerlab-block>` at root after the
 *   current block.
 * - when pressing Backspace, it either deletes a character, or delete the
 *   current `<steerlab-block>`, when in first position. It cannot delete a
 *   `<steerlab-question>`, in any case.
 * - when pressing Delete, it either deletes a character, or delete the next
 *   `<steerlab-block>`, when in last position. It cannot delete a
 *   `<steerlab-question>`, in any case.
 */

function Enter({ editor }: { editor: Editor }) {
  if (import.meta.env.DEV) console.log('[editor] handleEnter')
  const { type, block } = findClosestBlock(editor)

  // No node has been found, accept the newline.
  if (type === 'none') return false
  // Cursor is not at end of a node, accept the newline.
  if (!isEnd(editor, block)) return false
  // Compute the next position to insert a new node.
  const after = (() => {
    if (type === 'block') return block.from + block.size - 1
    if (type === 'answer') {
      const parent = block.closest('steerlabQuestion')
      if (!parent) return
      return parent.from + parent.size - 1
    }
  })()
  const id = crypto.randomUUID()
  const content = `<steerlab-block data-id="${id}"><p></p></steerlab-block>`
  if (after) editor.commands.insertContentAt(after, content)
  return true
}

function Backspace({ editor }: { editor: Editor }) {
  if (import.meta.env.DEV) console.log('[editor] handleBackspace')
  const { type, block } = findClosestBlock(editor)

  // No node has been found, accept the deletion.
  if (type === 'none') return false
  // Cursor is not at start of a node, accept the deletion.
  if (!isStart(editor, block)) return false
  // Cursor is at the top of the document, preventing.
  if (block.pos === 1) return true
  // Node is an answer, prevent deletion.
  if (type === 'answer') return true
  // Node is not editable, prevent deletion.
  if (block.node.attrs.editable === 'false') return true

  // Previous node should be editable too.
  if (block.before?.node.attrs.editable === 'false') return true

  // Previous node should not be a question or node should be empty.
  if (block.before?.node.type.name === 'steerlabQuestion') {
    if (block.textContent !== '') return true
    editor.commands.deleteRange({ from: block.pos - 4, to: block.pos + 1 })
    return true
  }

  editor.commands.deleteRange({ from: block.pos - 3, to: block.pos + 1 })
  return true
}

function Delete({ editor }: { editor: Editor }) {
  if (import.meta.env.DEV) console.log('[editor] handleDelete')
  const { type, block } = findClosestBlock(editor)

  // No node has been found, accept the deletion.
  if (type === 'none') return false
  // Cursor is not at end of a node, accept the deletion.
  if (!isEnd(editor, block)) return false
  // Node is not editable, prevent deletion.
  if (block.node.attrs.editable === 'false') return true

  // Next node should be editable too.
  if (block.after?.node.attrs.editable === 'false') return true

  // Next node should not be a question.
  if (type === 'block' && block.after?.node.type.name === 'steerlabQuestion')
    return true
  if (type === 'answer') {
    const parent = block.closest('steerlabQuestion')
    if (parent?.after?.node.type.name === 'steerlabQuestion') return true
    editor.commands.deleteRange({ from: block.to - 3, to: block.to + 2 })
    return true
  }

  editor.commands.deleteRange({ from: block.to - 2, to: block.to + 2 })
  return true
}

/* Helper sections. Reused in handlers. */
function isStart(editor: Editor, node: NodePos) {
  const startIndex = node.pos
  const toIndex = editor.state.selection.to - 1
  return startIndex === toIndex
}

function isEnd(editor: Editor, node: NodePos) {
  const endIndex = node.pos + node.node.content.size
  const toIndex = editor.state.selection.to + 1
  return endIndex === toIndex
}
